import { useCallback } from 'react';
import { useEditor, masterList, masterLetter, activeMasterOf, pageMasterOf } from '../context/EditorContext';
import { toast } from 'sonner';

/**
 * Master page management (Publisher: Page Design ▸ Master Pages, the Master Page ribbon tab and the Pages pane in
 * Master Page view): open / close the view, add, duplicate, rename, delete and apply masters to pages.
 * Every document change goes through `dispatch`, so it is one undoable step.
 */
export const useMasterPages = () => {
  const { state, stateRef, dispatch } = useEditor();
  const masters = masterList(state.masters);
  const isMaster = state.viewMode === 'master';
  const active = activeMasterOf(state);
  const letterOf = useCallback((id) => masterLetter(stateRef.current.masters, id), [stateRef]);

  /** Open Master Page view on `id` (default: the current page's master). */
  const edit = useCallback((id) => {
    const s = stateRef.current;
    const target = id && s.masters[id] ? id : (pageMasterOf(s, s.pages[s.currentPageIndex])?.id || masterList(s.masters)[0]?.id);
    dispatch({ type: 'EDIT_MASTER', payload: { id: target } });
  }, [dispatch, stateRef]);

  const close = useCallback(() => dispatch({ type: 'SET_VIEW_MODE', payload: 'normal' }), [dispatch]);

  const add = useCallback((name) => {
    dispatch({ type: 'ADD_MASTER', payload: { name, edit: true } });
    const s = stateRef.current;
    toast.success(`New master page added — you are now editing it`, { description: `${masterList(s.masters).length + 1} master pages. Apply it to pages from the Pages pane or the Master Page tab.` });
  }, [dispatch, stateRef]);

  const duplicate = useCallback((id) => {
    const s = stateRef.current;
    const src = s.masters[id];
    if (!src) return;
    dispatch({ type: 'ADD_MASTER', payload: { duplicateOf: id, edit: true } });
    toast.success(`Duplicated ${src.name}`, { description: 'The copy is open for editing.' });
  }, [dispatch, stateRef]);

  const rename = useCallback((id, name) => {
    if (!(name || '').trim()) return false;
    dispatch({ type: 'RENAME_MASTER', payload: { id, name } });
    return true;
  }, [dispatch]);

  const remove = useCallback((id) => {
    const s = stateRef.current;
    const list = masterList(s.masters);
    const m = s.masters[id];
    if (!m) return false;
    if (list.length <= 1) { toast.error('A publication needs at least one master page.'); return false; }
    const used = s.pages.filter((p) => p.masterId === id).length;
    const fallback = list.find((x) => x.id !== id);
    dispatch({ type: 'DELETE_MASTER', payload: { id } });
    toast.success(`Deleted ${m.name}`, { description: used ? `${used} page${used === 1 ? '' : 's'} now use ${fallback.name}. Undo (Ctrl+Z) brings it back.` : 'Undo (Ctrl+Z) brings it back.' });
    return true;
  }, [dispatch, stateRef]);

  /** Apply a master (or 'none') to one page (default: current) or to all pages. */
  const apply = useCallback((masterId, { pageIndex, all = false } = {}) => {
    const s = stateRef.current;
    const idx = pageIndex ?? s.currentPageIndex;
    dispatch({ type: 'APPLY_MASTER', payload: { masterId, pageIndex: idx, all } });
    const name = masterId === 'none' ? 'no master page' : s.masters[masterId]?.name || 'master page';
    toast.success(all ? `All ${s.pages.length} pages now use ${name}` : `${s.pages[idx]?.name || 'Page'} now uses ${name}`);
  }, [dispatch, stateRef]);

  return { masters, isMaster, active, letterOf, edit, close, add, duplicate, rename, remove, apply };
};
